import React, { useState, useEffect } from 'react';
import { Button } from '@/components/common';
import { Toast, ToastType } from '@/components/ui';
import { useStudents } from '@/hooks/data/useStudents';
import { useCurrentUser } from '@/hooks/auth/useCurrentUser';
import { useAttendance } from '@/hooks/data/useAttendance';
import './BulkAttendanceForm.css';

interface BulkAttendanceFormProps {
  selectedClass: string;
  selectedDate: string;
  isDateDisabled?: boolean;
  dateErrorMessage?: string;
  onAttendanceSaved: () => void;
}

interface StudentAttendance {
  studentId: string;
  rollNumber: string;
  name: string;
  status: 'present' | 'absent' | 'late' | 'unmarked';
  remarks: string;
}

const BulkAttendanceForm: React.FC<BulkAttendanceFormProps> = ({
  selectedClass,
  selectedDate,
  isDateDisabled = false,
  dateErrorMessage = '',
  onAttendanceSaved
}) => {
  // Use custom hooks for data
  const { students, loading } = useStudents({
    className: selectedClass !== 'all' ? selectedClass : undefined
  });
  const { userData: currentUser } = useCurrentUser();
  const { getAttendanceByClassAndDate, markAttendance } = useAttendance({ autoFetch: false });
  
  // Component state
  const [studentAttendance, setStudentAttendance] = useState<StudentAttendance[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [toast, setToast] = useState<{ isOpen: boolean; message: string; type: ToastType }>({
    isOpen: false,
    message: '',
    type: 'info'
  });

  // Initialize and update attendance data when students or date changes
  useEffect(() => {
    if (selectedClass === 'all' || students.length === 0) {
      setStudentAttendance([]);
      return;
    }

    const initializeAttendance = async () => {
      setError('');
      
      // Initialize attendance data from students
      const attendanceData: StudentAttendance[] = students.map(student => ({
        studentId: student.rollNumber || '',
        rollNumber: student.rollNumber || '',
        name: `${student.firstName} ${student.lastName}`,
        status: 'unmarked',
        remarks: ''
      }));

      // Load existing attendance for the date
      try {
        const existingAttendance = await getAttendanceByClassAndDate(selectedClass, selectedDate);
        if (existingAttendance && existingAttendance.students) {
          // Update attendance data with existing records
          attendanceData.forEach(attendance => {
            const existingRecord = existingAttendance.students.find(
              s => s.studentId === attendance.studentId
            );
            if (existingRecord) {
              attendance.status = existingRecord.status;
              attendance.remarks = existingRecord.remarks || '';
            }
          });
        }
      } catch (attendanceError) {
        console.log('No existing attendance found for this date');
      }

      setStudentAttendance(attendanceData);
    };

    initializeAttendance();
  }, [students, selectedClass, selectedDate, getAttendanceByClassAndDate]);

  const updateStudentStatus = (studentId: string, status: 'present' | 'absent' | 'late' | 'unmarked') => {
    setStudentAttendance(prev => 
      prev.map(student => 
        student.studentId === studentId 
          ? { ...student, status }
          : student
      )
    );
  };

  const updateStudentRemarks = (studentId: string, remarks: string) => {
    setStudentAttendance(prev => 
      prev.map(student => 
        student.studentId === studentId 
          ? { ...student, remarks }
          : student
      )
    );
  };

  const bulkSelectAll = (status: 'present' | 'absent' | 'late') => {
    setStudentAttendance(prev => 
      prev.map(student => ({ ...student, status }))
    );
  };

  const saveAttendance = async () => {
    // Check if date is disabled (holiday/Sunday/out of range)
    if (isDateDisabled) {
      setError(dateErrorMessage || 'Cannot mark attendance for this date');
      setToast({
        isOpen: true,
        message: dateErrorMessage || 'Cannot mark attendance for this date',
        type: 'error'
      });
      setSaving(false);
      return;
    }

    if (selectedClass === 'all') {
      setError('Please select a specific class');
      return;
    }

    if (!currentUser) {
      setError('User not authenticated');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Filter out unmarked students
      const markedStudents = studentAttendance.filter(student => student.status !== 'unmarked');
      
      if (markedStudents.length === 0) {
        setError('Please mark attendance for at least one student');
        setSaving(false);
        return;
      }

      // Prepare attendance data
      const attendanceData = {
        class: selectedClass,
        date: selectedDate,
        students: markedStudents.map(student => ({
          studentId: student.studentId,
          rollNumber: student.rollNumber,
          status: student.status as 'present' | 'absent' | 'late',
          remarks: student.remarks
        })),
        markedBy: {
          userId: currentUser.id,
          name: `${currentUser.firstName} ${currentUser.lastName}`,
          email: currentUser.email
        },
        createdAt: new Date().toISOString()
      };

      // Save attendance
      await markAttendance(attendanceData);
      
      setSuccess(`Attendance saved successfully for ${markedStudents.length} students`);
      setToast({
        isOpen: true,
        message: `Attendance saved successfully for ${markedStudents.length} students on ${new Date(selectedDate).toLocaleDateString()}!`,
        type: 'success'
      });
      onAttendanceSaved();
      
      // Reset all students to unmarked state and clear remarks
      setStudentAttendance(prev => 
        prev.map(student => ({
          ...student,
          status: 'unmarked',
          remarks: ''
        }))
      );
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);

    } catch (err) {
      console.error('Error saving attendance:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save attendance. Please try again.';
      setError(errorMessage);
      setToast({
        isOpen: true,
        message: errorMessage,
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'present': return '#10b981';
      case 'absent': return '#ef4444';
      case 'late': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'present': return '✅';
      case 'absent': return '❌';
      case 'late': return '⏰';
      default: return '❓';
    }
  };

  if (loading) {
    return (
      <div className="bulk-attendance-loading">
        <div className="spinner"></div>
        <p>Loading students...</p>
      </div>
    );
  }

  if (selectedClass === 'all') {
    return (
      <div className="bulk-attendance-placeholder">
        <p>Please select a specific class to mark attendance</p>
      </div>
    );
  }

  if (isDateDisabled && dateErrorMessage) {
    return (
      <div className="bulk-attendance-disabled">
        <div className="disabled-icon">🚫</div>
        <h3>Attendance Not Available</h3>
        <p className="disabled-message">{dateErrorMessage}</p>
        <div className="disabled-info">
          <h4>Restrictions:</h4>
          <ul>
            <li>📅 Can only edit attendance for the last 3 days</li>
            <li>☀️ Sundays are non-working days</li>
            <li>🏖️ Holidays are non-working days</li>
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="bulk-attendance-form">
      {/* Toast Notification */}
      <Toast
        message={toast.message}
        type={toast.type}
        isOpen={toast.isOpen}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />

      <div className="attendance-header">
        <h3>📋 Bulk Attendance - {selectedClass.toUpperCase()}</h3>
        <div className="attendance-info">
          <span className="date-info">📅 {new Date(selectedDate).toLocaleDateString()}</span>
          <span className="count-info">👥 {students.length} Students</span>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      {success && (
        <div className="success-banner">
          <span className="success-icon">✅</span>
          {success}
        </div>
      )}

      {/* Bulk Actions */}
      <div className="bulk-actions">
        <h4>Quick Actions:</h4>
        <div className="bulk-buttons">
          <Button
            variant="secondary"
            onClick={() => bulkSelectAll('present')}
            className="bulk-btn present"
            disabled={isDateDisabled}
          >
            ✅ Mark All Present
          </Button>
          <Button
            variant="secondary"
            onClick={() => bulkSelectAll('absent')}
            className="bulk-btn absent"
            disabled={isDateDisabled}
          >
            ❌ Mark All Absent
          </Button>
          <Button
            variant="secondary"
            onClick={() => bulkSelectAll('late')}
            className="bulk-btn late"
            disabled={isDateDisabled}
          >
            ⏰ Mark All Late
          </Button>
        </div>
      </div>

      {/* Desktop Attendance Table */}
      <div className="attendance-table-container">
        <table className="attendance-table">
          <thead>
            <tr>
              <th>Roll No.</th>
              <th>Student Name</th>
              <th>Status</th>
              <th>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {studentAttendance.map((student) => (
              <tr key={student.studentId}>
                <td className="roll-number">{student.rollNumber}</td>
                <td className="student-name">{student.name}</td>
                <td className="status-cell">
                  <div className="status-buttons">
                    <button
                      className={`status-btn present ${student.status === 'present' ? 'active' : ''}`}
                      onClick={() => updateStudentStatus(student.studentId, 'present')}
                      disabled={isDateDisabled}
                    >
                      ✅ Present
                    </button>
                    <button
                      className={`status-btn absent ${student.status === 'absent' ? 'active' : ''}`}
                      onClick={() => updateStudentStatus(student.studentId, 'absent')}
                      disabled={isDateDisabled}
                    >
                      ❌ Absent
                    </button>
                    <button
                      className={`status-btn late ${student.status === 'late' ? 'active' : ''}`}
                      onClick={() => updateStudentStatus(student.studentId, 'late')}
                      disabled={isDateDisabled}
                    >
                      ⏰ Late
                    </button>
                  </div>
                  <div className="current-status">
                    <span 
                      className="status-indicator"
                      style={{ color: getStatusColor(student.status) }}
                    >
                      {getStatusIcon(student.status)} {student.status.toUpperCase()}
                    </span>
                  </div>
                </td>
                <td className="remarks-cell">
                  <input
                    type="text"
                    placeholder="Add remarks..."
                    value={student.remarks}
                    onChange={(e) => updateStudentRemarks(student.studentId, e.target.value)}
                    className="remarks-input"
                    disabled={isDateDisabled}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Attendance Cards */}
      <div className="mobile-attendance-cards">
        {studentAttendance.map((student) => (
          <div key={student.studentId} className="mobile-student-card">
            <div className="mobile-student-header">
              <div className="mobile-student-info">
                <div className="mobile-roll-number">{student.rollNumber}</div>
                <div className="mobile-student-name">{student.name}</div>
              </div>
              <div className={`mobile-current-status ${student.status}`}>
                {getStatusIcon(student.status)} {student.status.toUpperCase()}
              </div>
            </div>

            <div className="mobile-status-section">
              <div className="mobile-status-title">Mark Status:</div>
              <div className="mobile-status-buttons">
                <button
                  className={`mobile-status-btn present ${student.status === 'present' ? 'active' : ''}`}
                  onClick={() => updateStudentStatus(student.studentId, 'present')}
                  disabled={isDateDisabled}
                >
                  <span className="mobile-status-icon">✅</span>
                  <span>Present</span>
                </button>
                <button
                  className={`mobile-status-btn absent ${student.status === 'absent' ? 'active' : ''}`}
                  onClick={() => updateStudentStatus(student.studentId, 'absent')}
                  disabled={isDateDisabled}
                >
                  <span className="mobile-status-icon">❌</span>
                  <span>Absent</span>
                </button>
                <button
                  className={`mobile-status-btn late ${student.status === 'late' ? 'active' : ''}`}
                  onClick={() => updateStudentStatus(student.studentId, 'late')}
                  disabled={isDateDisabled}
                >
                  <span className="mobile-status-icon">⏰</span>
                  <span>Late</span>
                </button>
              </div>
            </div>

            <div className="mobile-remarks-section">
              <div className="mobile-remarks-title">Remarks:</div>
              <input
                type="text"
                placeholder="Add remarks..."
                value={student.remarks}
                onChange={(e) => updateStudentRemarks(student.studentId, e.target.value)}
                className="mobile-remarks-input"
                disabled={isDateDisabled}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Summary and Save */}
      <div className="attendance-summary">
        <div className="summary-stats">
          <div className="stat-item">
            <span className="stat-label">Present:</span>
            <span className="stat-value present">
              {studentAttendance.filter(s => s.status === 'present').length}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Absent:</span>
            <span className="stat-value absent">
              {studentAttendance.filter(s => s.status === 'absent').length}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Late:</span>
            <span className="stat-value late">
              {studentAttendance.filter(s => s.status === 'late').length}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Unmarked:</span>
            <span className="stat-value unmarked">
              {studentAttendance.filter(s => s.status === 'unmarked').length}
            </span>
          </div>
        </div>

        <Button
          variant="primary"
          onClick={saveAttendance}
          disabled={saving || studentAttendance.filter(s => s.status !== 'unmarked').length === 0 || isDateDisabled}
          className="save-attendance-btn"
        >
          {saving ? (
            <>
              <span className="spinner"></span>
              Saving...
            </>
          ) : (
            <>
              <span className="btn-icon">💾</span>
              Save Attendance ({studentAttendance.filter(s => s.status !== 'unmarked').length} students)
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default BulkAttendanceForm;
